import { useEffect, useState } from 'react'
import type { SystemPromptSaveState, SystemPromptState } from '@/hooks/useSystemPrompt'
import styles from './SystemPromptEditor.module.css'

export type SystemPromptEditorProps = {
  state: SystemPromptState
  saveState: SystemPromptSaveState
  onSave: (prompt: string) => void
}

export default function SystemPromptEditor({ state, saveState, onSave }: SystemPromptEditorProps) {
  const [draft, setDraft] = useState('')

  // Seed the textarea from the loaded prompt, and re-seed after a save resolves
  // (server-confirmed value). `state` only changes on load/save, never while
  // typing, so this never clobbers an in-progress edit.
  useEffect(() => {
    if (state.kind === 'ready') {
      setDraft(state.prompt)
    }
  }, [state])

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    onSave(draft)
  }

  const saving = saveState.kind === 'saving'

  return (
    <section className={styles.section} aria-labelledby="system-prompt-heading">
      <h2 id="system-prompt-heading" className={styles.heading}>
        Systemprompt
      </h2>
      <p className={styles.lead}>
        Teksten her legges til OTilies grunnprompt og styrer hvordan assistenten svarer. Endringer
        gjelder nye samtaler.
      </p>

      {renderBody(state, draft, setDraft, handleSubmit, saving)}

      {renderStatus(state, saveState)}
    </section>
  )
}

function renderBody(
  state: SystemPromptState,
  draft: string,
  setDraft: (value: string) => void,
  onSubmit: (e: React.FormEvent) => void,
  saving: boolean,
): React.ReactNode {
  if (state.kind === 'loading') {
    return <p className={styles.muted}>Laster systemprompt…</p>
  }
  if (state.kind === 'error') {
    return <p className={styles.error}>Kunne ikke laste systemprompt: {state.message}</p>
  }
  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <label className={styles.label} htmlFor="system-prompt-input">
        Instruksjoner til OTilie
      </label>
      <textarea
        id="system-prompt-input"
        className={styles.textarea}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={12}
        spellCheck={false}
        placeholder="Skriv inn instruksjoner til OTilie…"
      />
      <div className={styles.help}>
        <p className={styles.helpLead}>
          Du kan styre <strong>lengde</strong>, <strong>detaljnivå</strong>, hva som skal{' '}
          <strong>prioriteres eller utelates</strong>, <strong>struktur</strong> og{' '}
          <strong>tone</strong> (innenfor profesjonelle rammer).
        </p>
        <p className={styles.helpNote}>
          OTilie siterer alltid kilder, svarer kun ut fra dokumentene, holder seg profesjonell og
          svarer på språket brukeren spør på — dette kan ikke overstyres.
        </p>
        <p className={styles.helpExample}>
          Eksempel: «Svar mer utfyllende og inkluder konkrete eksempler der det er relevant.»
        </p>
      </div>
      <div className={styles.actions}>
        <button type="submit" className={styles.saveButton} disabled={saving}>
          {saving ? 'Lagrer…' : 'Lagre systemprompt'}
        </button>
      </div>
    </form>
  )
}

function renderStatus(
  state: SystemPromptState,
  saveState: SystemPromptSaveState,
): React.ReactNode {
  if (saveState.kind === 'saved') {
    return (
      <p className={styles.success} role="status">
        Systemprompt lagret.
      </p>
    )
  }
  if (saveState.kind === 'error') {
    return (
      <p className={styles.saveError} role="status">
        Kunne ikke lagre: {saveState.message}
      </p>
    )
  }
  if (state.kind === 'ready' && state.submittedBy !== '') {
    return <p className={styles.meta}>Sist endret av {state.submittedBy}</p>
  }
  return null
}
